<template>
    <div>
        <h3>{{ $t("settings.channels.Authentication") }}</h3>
        <p>{{ $t("settings.channels.twitter.explainer") }}</p>

        <article v-if="channelData.identity === 'disconnected'">
            <b-card class="border">
                <template #header>
                    <h3>{{ $t("settings.channels.twitter.step1.title") }}</h3>
                </template>

                <p>{{ $t("settings.channels.twitter.step1.explainer") }}</p>
                <b-button name="twitter-connect" variant="primary" @click="connect">
                    <b-spinner v-if="isConnecting" small></b-spinner>
                    {{ $t("settings.channels.twitter.connect") }}
                </b-button>
            </b-card>
            <b-card class="border">
                <template #header>
                    <h3>{{ $t("settings.channels.twitter.step2.title") }}</h3>
                </template>

                <p>{{ $t("settings.channels.twitter.step2.explainer") }}</p>
                <b-form-group
                    id="fieldset-twitter-pin"
                    :description="$t('settings.channels.twitter.pin.description')"
                    :label="$t('settings.channels.twitter.pin.label')"
                    content-cols-lg="7"
                    content-cols-sm
                    label-cols-lg="3"
                    label-cols-sm="4"
                    label-for="identity"
                >
                    <b-input-group>
                        <b-form-input v-model="pin" name="twitter-pin"></b-form-input>
                        <b-button name="twitter-pin-submit" variant="primary" @click="authenticate">
                            <b-spinner v-if="isAuthenticating" small></b-spinner>
                            {{ $t("settings.channels.twitter.pin.submit") }}
                        </b-button>
                    </b-input-group>
                </b-form-group>
            </b-card>
        </article>
        <article v-else>
            <b-form-group
                id="fieldset-twitter-handle"
                :label="$t('settings.channels.twitter.handle.label')"
                content-cols-lg="7"
                content-cols-sm
                label-cols-lg="3"
                label-cols-sm="4"
                label-for="identity"
            >
                @{{ auth.name }}
                <b-button class="btn-icon" variant="flat" @click="doCopy">
                    <font-awesome-icon class="ml-50" icon="copy" />
                </b-button>
            </b-form-group>

            <b-button name="twitter-disconnect" variant="primary" @click="disconnect">
                <b-spinner v-if="isDisconnecting" small></b-spinner>
                {{ $t("settings.channels.twitter.disconnect") }}
            </b-button>
        </article>
        <hr />
    </div>
</template>
<script>
import { BCard, BButton, BFormGroup, BSpinner, BInputGroup, BFormInput } from "bootstrap-vue";
import axios from "@/libs/axios";
import { useToast } from "vue-toastification/composition";

export default {
    components: {
        BButton,
        BCard,
        BFormGroup,
        BSpinner,
        BInputGroup,
        BFormInput,
    },
    props: {
        channelData: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isConnecting: false,
            isAuthenticating: false,
            isDisconnecting: false,
            token: "",
            pin: "",
        };
    },
    methods: {
        doCopy() {
            this.$copyText(`@${this.auth.name}`).then(
                () => {
                    this.toast.success(this.$t("copied"));
                },
                () => {
                    this.toast.error(this.$t("not-copied"));
                }
            );
        },
        connect() {
            this.isConnecting = true;

            axios
                .post(`channels/${this.channelData.id}/connect`)
                .then((tokenData) => {
                    const tokens = tokenData.data;

                    if (!tokens.oauth_callback_confirmed) {
                        throw new Error("Callback unconfirmed");
                    }

                    this.token = tokens.oauth_token;
                    try {
                        window
                            .open(`https://api.twitter.com/oauth/authorize?oauth_token=${tokens.oauth_token}`, "_blank")
                            .focus();
                    } catch {
                        // It's likely popups were blocked by the client. When they allow them the redirect will still happen.
                        // So we just notify the user that they should allow popups
                        this.toast.warning(this.$t("settings.channels.twitter.connecting.popup-blocked"));
                    }
                })
                .catch((error) => {
                    this.toast.error(this.$t("settings.channels.twitter.connecting.failure"));
                })
                .finally(() => (this.isConnecting = false));
        },
        authenticate() {
            this.isAuthenticating = true;

            axios
                .post(`channels/${this.channelData.id}/authenticate`, {
                    pin: this.pin,
                    token: this.token,
                })
                .then((newChannelData) => {
                    this.channelData.identity = newChannelData.data.identity;
                    this.channelData.auth = newChannelData.data.auth;

                    this.token = "";
                    this.pin = "";
                    this.toast.success(this.$t("settings.channels.twitter.authenticating.success"));
                })
                .catch(() => {
                    this.toast.error(this.$t("settings.channels.twitter.authenticating.failure"));
                })
                .finally(() => (this.isAuthenticating = false));
        },
        disconnect() {
            this.isDisconnecting = true;

            axios
                .post(`channels/${this.channelData.id}/disconnect`)
                .then((newChannelData) => {
                    this.channelData.identity = newChannelData.data.identity;
                    this.channelData.auth = newChannelData.data.auth;

                    this.token = "";
                    this.pin = "";
                    this.toast.success(this.$t("settings.channels.twitter.disconnecting.success"));
                })
                .catch(() => {
                    this.toast.error(this.$t("settings.channels.twitter.disconnecting.failure"));
                })
                .finally(() => (this.isDisconnecting = false));
        },
    },
    setup(props) {
        const auth = JSON.parse(props.channelData.auth);
        const toast = useToast();

        return {
            auth,
            toast,
        };
    },
};
</script>
